import { isActivityClaimPath } from './pages/activityClaim/navigation'
/*
 * Copyright © 2020-2022 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2025-09-16 17:15:07
 */
/* eslint-disable */

/**
 * 照片搜索功能排序状态管理：
 * - 水印内容标签页：使用独立的Redux状态(state.photosByContent.isAscSort)
 * - 精准搜索标签页：使用独立的Redux状态(state.photosByPrecise.isAscSort)
 * 通过独立的状态管理，确保两个标签页的排序状态互不影响
 */

import React, { lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import { ConfigProvider } from 'antd'
import zhCN from 'antd/es/locale/zh_CN'
// dayjs 已在 utils/dayjs.ts 中配置中文语言包
import './utils/dayjs'
import './styles/index.css'
import './styles/App.scss'
import 'animate.css'

import * as Sentry from '@sentry/react'
import { Integrations } from '@sentry/tracing'

import { store } from './app/store'

import getUA from './utils/getUA'
import sensors from './utils/sensors'

import App from './App'
import LegacyKernelCompat from './components/LegacyKernelCompat'
import desktopLib from './utils/desktopLib'
// 登录页轮播首图（LCP 元素），用于下方桌面分支的 preload 预热
import loginHeroImg from './images/login/login_arousel_1.png'
// 引入客服抽屉全局服务

// UA 分流的移动端/微信引导页按需拆包：桌面主应用不加载这两个入口的代码
const ActivityClaimPage = lazy(() => import('./pages/activityClaim'))
const Mobile = lazy(() => import('./pages/Mobile'))
const OpenInWX = lazy(() => import('./pages/OpenInWX'))

sensors.track('web_workgroup_pageview')
sensors.track('user_act_valid', { actionType: 'web活跃' })

Sentry.init({
  dsn: 'https://4402576e5f1e61a4e2a8a7eb6d334af5@sentry-v2.xhey.top/2',
  ignoreErrors: [
    'onLayoutChanged is not defined',
    'global code(<unknown module>)',
    'Load timeout for modules'
  ],
  denyUrls: [
    'https://localhost',
    'https://webapi.amap.com',
    'https://ssl.captcha.qq.com',
    'https://vdata.amap.com',
    /^file:\/\//i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i
  ],
  integrations: [
    new Integrations.BrowserTracing(),
    new Sentry.Integrations.Breadcrumbs({
      console: false
    })
  ],
  tracesSampleRate: 1.0,
  environment: __RELEASE_STAGE__,
  release: __VERSION__,
  normalizeDepth: 0
})

window._AMapSecurityConfig = {
  serviceHost: __AMAP_SERVICE_SERVER__
}

// Promise.finally polyfill（现代浏览器已内置，保留兼容旧浏览器）
if (!Promise.prototype.finally) {
  Promise.prototype.finally = function <T>(
    this: Promise<T>,
    onFinally: () => void
  ): Promise<T> {
    return this.then(
      (result) => Promise.resolve(onFinally()).then(() => result),
      (reason) =>
        Promise.resolve(onFinally()).then(() => Promise.reject(reason))
    )
  }
}

Sentry.configureScope((scope) => {
  scope.setTag('isDesktop', !!desktopLib.isDesktop)
  // 重构版标识：全量后新旧版共用主域名，靠该 tag 区分 Sentry 异常/性能采集来自重构版还是旧版（旧版不带此 tag）
  scope.setTag('webEdition', 'refactor2026')
  if (desktopLib.isDesktop) {
    scope.setContext('ClientInfo', desktopLib.clientInfo)
  }
})

if (desktopLib.isDesktop) {
  // 桌面客户端禁用新窗口打开，改为当前窗口跳转
  window.open = (url?: string | URL): Window | null => {
    if (url) {
      window.location.href = url.toString()
    }
    return null
  }
}

const container = document.getElementById('root')!
const root = createRoot(container)

const activityClaimFlow = isActivityClaimPath(window.location.pathname)
if (activityClaimFlow) {
  root.render(
    <BrowserRouter>
      <Suspense fallback={null}>
        <ActivityClaimPage />
      </Suspense>
    </BrowserRouter>
  )
} else if (getUA().isMobile) {
  root.render(
    <Suspense fallback={null}>
      <Mobile />
    </Suspense>
  )
} else if (getUA().isWeiXin) {
  root.render(
    <Suspense fallback={null}>
      <OpenInWX />
    </Suspense>
  )
} else {
  // 预热登录页轮播首图（LCP 元素）：鉴权期间（user 尚为 null）登录页对所有
  // 访客先行渲染、该图必然会被请求；提前 preload 使其与登录 chunk 并行下载，
  // 而不是等 chunk 执行后才被发现，可明显缩短首屏 LCP。
  const heroPreload = document.createElement('link')
  heroPreload.rel = 'preload'
  heroPreload.setAttribute('as', 'image')
  heroPreload.setAttribute('fetchpriority', 'high')
  heroPreload.href = loginHeroImg
  document.head.appendChild(heroPreload)

  root.render(
    <LegacyKernelCompat>
      <ConfigProvider locale={zhCN}>
        <Provider store={store}>
          <BrowserRouter
            future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
          >
            <App />
          </BrowserRouter>
        </Provider>
      </ConfigProvider>
    </LegacyKernelCompat>
  )
}
